import * as StateLib from "@/Utils/StateLib";
import { isNullish, Menu, OverflowText, ToolIcon } from "@theorgchartdev/storybook";
import React from "react";
import { GetWSMgr } from "@/Utils/StateLib";
import { useAppNavigate } from "@/hooks/useAppNavigate";
import { SwitchWrapper } from "@/appComponents/Blocks/Menus/FieldToggleMenu/FieldToggleMenu";
import { useAutoSaveHandler } from "@/hooks/useAutoSaveHandler";
import { useAutoSyncHandler } from "@/hooks/useAutoSyncHandler";
import cx from "classnames";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import { useRenameDocController } from "@/hooks/useRenameDocController";
import { Globals } from "@coreapp/CoreApp/Globals";
import { enumFileMgrMode, enumChartOpenModes } from "@coreapp/constants";
import { appstr } from "@coreapp/utils/appstr";
import { isInReporting } from "@coreapp/utils/isInReporting";
import { isInBoxLayoutEditor } from "@coreapp/utils/isInBoxLayoutEditor";
import DocDetailsEditModal from "@/appComponents/Blocks/Modals/DocDetailsEditModal/DocDetailsEditModal";

const actionsEnum = {
  SAVE: 0,
  SAVE_AS: 1,
  RENAME: 2,
  RETURN_TO_BASE: 3,
  AUTO_SAVE: 4,
  AUTO_SYNC: 5,
  ABOUT: 6,
  BASE_CHART_SETTINGS: 7,
  EDIT_DETAILS: 8
};

const enumRenameLabel = {
  PLAN: "Rename plan",
  BASE: "Rename base chart",
  CUSTOM: "Rename custom chart",
  REPORT: "Rename report"
};

const enumDetailsEntityName = {
  PLAN: "Plan",
  BASE: "Base Chart",
  CUSTOM: "Custom Chart"
};

const enumSaveLabel = {
  DEFAULT: "Save",
  LAYOUT_EDITOR: "Save and exit"
};

const modeActions = {
  report: [actionsEnum.SAVE, actionsEnum.RENAME, actionsEnum.RETURN_TO_BASE],
  baseChart: [
    actionsEnum.EDIT_DETAILS,
    actionsEnum.RENAME,
    actionsEnum.BASE_CHART_SETTINGS,
    actionsEnum.ABOUT
  ],
  custom: {
    full: [
      actionsEnum.EDIT_DETAILS,
      actionsEnum.SAVE,
      actionsEnum.SAVE_AS,
      actionsEnum.RENAME,
      actionsEnum.RETURN_TO_BASE,
      actionsEnum.AUTO_SAVE,
      actionsEnum.AUTO_SYNC,
      actionsEnum.ABOUT
    ],
    limited: [actionsEnum.RETURN_TO_BASE, actionsEnum.ABOUT]
  },
  plan: [
    actionsEnum.EDIT_DETAILS,
    actionsEnum.RENAME,
    actionsEnum.RETURN_TO_BASE,
    actionsEnum.ABOUT
  ],
  layouteditor: [actionsEnum.SAVE]
};

export const QuickAccessMenu = ({ doc, handleRef }) => {
  const iWiz = doc?.GetIWizData();
  const wsMgr = GetWSMgr();
  const { canEditDocuments, canAccessChartSettings } = usePermissionCheck();
  const { canEdit } = useRenameDocController();
  const nav = useAppNavigate();
  const { autoSaveEnabled, disableToggle, toggleAutoSaveChart, handleSaveClick } =
    useAutoSaveHandler({ doc, iWiz });
  const { toggleLiveChart, disableLiveToggle, autoSyncEnabled } = useAutoSyncHandler({
    doc
  });
  const [showDetailsModal, setShowDetailsModal] = React.useState(false);

  const isReport = isInReporting();
  const isLayoutEditor = isInBoxLayoutEditor();
  const isBaseChart = doc?.IsServerChart();
  const isPlan = doc?.IsPlanChart();
  const readyToSave = wsMgr?.ReadyToSave();
  const chartID = doc?.GetChartID();

  const getRenameKey = () => {
    if (isReport) return enumRenameLabel.REPORT;
    return isBaseChart
      ? isPlan
        ? enumRenameLabel.PLAN
        : enumRenameLabel.BASE
      : enumRenameLabel.CUSTOM;
  };

  const getSaveKey = () => {
    if (isLayoutEditor) return enumSaveLabel.LAYOUT_EDITOR;
    else return enumSaveLabel.DEFAULT;
  };

  const getEditDetailsKey = () => {
    let entity;
    if (isReport || isLayoutEditor) return "";
    else {
      entity = isBaseChart
        ? isPlan
          ? enumDetailsEntityName.PLAN
          : enumDetailsEntityName.BASE
        : enumDetailsEntityName.CUSTOM;
    }

    return appstr("Edit entity details", { entity: entity.toLowerCase() });
  };

  const baseClass = "ocn-field-toggle-option";
  const textSize = "small";
  const checkedClass = "ocn-field-toggle-option-label--checked";

  const actionConditions = {
    [actionsEnum.BASE_CHART_SETTINGS]: () => !canAccessChartSettings,
    [actionsEnum.RENAME]: () => !canEdit(),
    [actionsEnum.EDIT_DETAILS]: () => !canEdit(),
    [actionsEnum.ABOUT]: () => !Globals._uiConfig?.fEnableAbout
  };

  const determineCurrentMode = () => {
    if (isLayoutEditor) return "layouteditor";
    if (isReport) return "report";
    if (isBaseChart) return isPlan ? "plan" : "baseChart";
    return "custom";
  };

  const allActions = {
    [actionsEnum.SAVE]: {
      label: appstr(getSaveKey()),
      clickHandler: handleSaveClick,
      disabled: isNullish(doc) || (!readyToSave && !isReport && !isLayoutEditor),
      dataTestId: "chart-mode-toolbar-save"
    },
    [actionsEnum.SAVE_AS]: {
      label: appstr("saveas"),
      clickHandler: () => {
        wsMgr.DoFileMgr(enumFileMgrMode.saveas);
      },
      disabled: isNullish(doc),
      dataTestId: "chart-mode-toolbar-save-as"
    },
    [actionsEnum.RENAME]: {
      label: appstr(getRenameKey()),
      clickHandler: () => {
        setTimeout(() => {
          handleRef.current?.edit();
        }, 0);
      },
      divider: !isBaseChart || doc?.IsPlanChart() || isReport,
      disabled: isNullish(doc),
      icon: "pen-line",
      dataTestId: "chart-mode-toolbar-rename"
    },
    [actionsEnum.RETURN_TO_BASE]: {
      label: appstr("Return to base chart"),
      icon: "share-arrow",
      clickHandler: () => {
        // For plans, doc.GetChartID() is the synced base chart id.
        // For custom charts and box layouts, it's the source base chart id.
        // Navigate to /base/{id} so the URL is the source of truth.
        const baseChartId = doc?.GetChartID();
        if (baseChartId != null) nav.toChart(String(baseChartId));
      },
      divider: doc?.IsPlanChart() && !isReport,
      dataTestId: "chart-mode-toolbar-return-to-base"
    },
    [actionsEnum.AUTO_SAVE]: {
      label: appstr("Auto-save"),
      content: (
        <span className={baseClass}>
          <span className={cx(`${baseClass}-label`, autoSaveEnabled && checkedClass)}>
            <OverflowText size={textSize}>{appstr("Auto-save")}</OverflowText>
          </span>
          <span className={`${baseClass}-switch`}>
            <SwitchWrapper
              checked={autoSaveEnabled}
              option={"auto-save"}
              subkey={"enabled"}
              label={"Auto-save"}
              id={"auto-save-switch"}
              onChange={toggleAutoSaveChart}
            />
          </span>
        </span>
      ),
      disabled: disableToggle,
      clickHandler: toggleAutoSaveChart,
      dataTestId: "chart-mode-toolbar-auto-save"
    },
    [actionsEnum.AUTO_SYNC]: {
      label: appstr("Auto-sync"),
      content: (
        <span className={baseClass}>
          <span className={cx(`${baseClass}-label`, autoSyncEnabled && checkedClass)}>
            <OverflowText size={textSize}>{appstr("Auto-sync")}</OverflowText>
          </span>
          <span className={`${baseClass}-switch`}>
            <SwitchWrapper
              checked={autoSyncEnabled}
              option={"auto-sync"}
              subkey={"enabled"}
              label={"Auto-sync"}
              id={"auto-sync-switch"}
              onChange={toggleLiveChart}
            />
          </span>
        </span>
      ),
      divider: true,
      disabled: disableLiveToggle,
      clickHandler: toggleLiveChart,
      dataTestId: "chart-mode-toolbar-auto-sync"
    },
    [actionsEnum.BASE_CHART_SETTINGS]: {
      label: appstr("Chart Settings"),
      clickHandler: () => {
        nav.navigateSettings("chart", chartID);
      },
      divider: Globals._uiConfig.fEnableAbout,
      dataTestId: "chart-mode-toolbar-chart-settings"
    },
    [actionsEnum.ABOUT]: {
      label: appstr("Chart Info"),
      icon: "circle-info",
      clickHandler: () => {
        nav.navigateDetails("chart");
      },
      disabled: isNullish(doc),
      dataTestId: "chart-mode-toolbar-about"
    },
    [actionsEnum.EDIT_DETAILS]: {
      label: getEditDetailsKey(),
      clickHandler: () => {
        setShowDetailsModal(true);
      },
      disabled: isNullish(doc),
      icon: "pen-to-square",
      dataTestId: "chart-mode-toolbar-edit-details"
    }
  };

  const getShownActions = () => {
    const currentMode = determineCurrentMode();
    let applicableActions =
      currentMode === "custom"
        ? modeActions.custom[canEditDocuments ? "full" : "limited"]
        : modeActions[currentMode];
    return applicableActions.filter(action => {
      const condition = actionConditions[action];
      return !condition || !condition();
    });
  };

  const shownActions = getShownActions();

  const options = shownActions.map(actionId => {
    const action = allActions[actionId];
    return {
      label: action.label,
      value: actionId,
      divider: action.divider,
      content: action.content,
      disabled: action.disabled,
      icon: action.icon,
      clickHandler: action.clickHandler,
      dataTestId: action.dataTestId
    };
  });

  if (shownActions.length === 0) return;

  return (
    <>
      <Menu
        trigger={<ToolIcon name={"ellipsis-vertical"} size={"medium-small"} shape={"round"} />}
        name={"ellipsis-vertical"}
        title={"Quick Access"}
        size="xsmall"
        listWidth={"medium"}
        shape={"round"}
        dataTestId={"quick-access-menu"}
        options={options}
        tooltipProps={"Quick access"}
        aria-label={"Quick access menu"}
        onClick={() => {
          const wsMgr = StateLib.GetWSMgr();
          const eventTracker = wsMgr.GetUsageTracker();
          eventTracker.ReportEvent("global.quick_access_menu.opened");
        }}
      />
      <DocDetailsEditModal
        show={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        initialData={{}}
        mode={determineCurrentMode()}
      />
    </>
  );
};
